if (typeof CountdownTimer === 'undefined') {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super()
      this.type = this.dataset.type || 'flip-clock'
      this.mode = this.dataset.mode || 'specific_date'
      this.expiredMessage = this.dataset.expiredMessage || 'Sale has ended'
      this.targetDate = null
      this.intervalId = null
      this.isExpired = false
    }

  connectedCallback() {
    if (this.dataset.type) {
      this.type = this.dataset.type
    }
    if (this.dataset.mode) {
      this.mode = this.dataset.mode
    }
    if (this.dataset.expiredMessage) {
      this.expiredMessage = this.dataset.expiredMessage
    }
    
    this.initializeTargetDate()
    this.render()
    this.startTimer()
  }

  disconnectedCallback() {
    this.stopTimer()
  }

  initializeTargetDate() {
    try {
      if (this.mode === 'specific_date') {
        const dateStr = (this.dataset.targetDate || '').trim()
        const timeStr = (this.dataset.targetTime || '23:59').trim()
        
        if (dateStr && dateStr !== '') {
          const dateParts = dateStr.split('-').filter(part => part !== '')
          const timeParts = timeStr.split(':').filter(part => part !== '')
          
          if (dateParts.length === 3) {
            const year = parseInt(dateParts[0], 10)
            const month = parseInt(dateParts[1], 10)
            const day = parseInt(dateParts[2], 10)
            const hours = timeParts.length > 0 ? (parseInt(timeParts[0], 10) || 0) : 0
            const minutes = timeParts.length > 1 ? (parseInt(timeParts[1], 10) || 0) : 0
            
            if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year > 0 && month > 0 && day > 0) {
              this.targetDate = new Date(year, month - 1, day, hours, minutes, 0, 0)
            } else {
              this.targetDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
            }
          } else {
            this.targetDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
          }
        } else {
          this.targetDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
        }
      } else {
        const days = parseInt(this.dataset.durationDays || '0', 10) || 0
        const hours = parseInt(this.dataset.durationHours || '0', 10) || 0
        const minutes = parseInt(this.dataset.durationMinutes || '0', 10) || 0
        
        const totalSeconds = (days * 24 * 60 * 60) + (hours * 60 * 60) + (minutes * 60)
        this.targetDate = new Date(Date.now() + totalSeconds * 1000)
      }
      
      if (!this.targetDate || isNaN(this.targetDate.getTime())) {
        this.targetDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
      }
    } catch (error) {
      console.error('Error initializing countdown target date:', error)
      this.targetDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
    }
  }

  calculateTimeRemaining() {
    if (!this.targetDate || isNaN(this.targetDate.getTime())) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: true
      }
    }

    const now = new Date()
    const diff = this.targetDate.getTime() - now.getTime()

    if (isNaN(diff) || diff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: true
      }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24)) || 0
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)) || 0
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)) || 0
    const seconds = Math.floor((diff % (1000 * 60)) / 1000) || 0

    return {
      days: isNaN(days) ? 0 : days,
      hours: isNaN(hours) ? 0 : hours,
      minutes: isNaN(minutes) ? 0 : minutes,
      seconds: isNaN(seconds) ? 0 : seconds,
      expired: false
    }
  }

  formatNumber(num) {
    const number = parseInt(num, 10) || 0
    if (isNaN(number)) {
      return '00'
    }
    return number.toString().padStart(2, '0')
  }

  render() {
    const time = this.calculateTimeRemaining()

    if (time.expired) {
      this.isExpired = true
      this.innerHTML = `<div class="countdown-timer__expired">${this.expiredMessage}</div>`
      this.stopTimer()
      return
    }

    this.isExpired = false

    switch (this.type) {
      case 'flip-clock':
        this.renderFlipClock(time)
        break
      case 'digital':
        this.renderDigital(time)
        break
      case 'text':
        this.renderText(time)
        break
    }
  }

  renderFlipClock(time) {
    const days = Number(time.days) || 0
    const hours = Number(time.hours) || 0
    const minutes = Number(time.minutes) || 0
    const seconds = Number(time.seconds) || 0
    
    const daysStr = this.formatNumber(days)
    const hoursStr = this.formatNumber(hours)
    const minutesStr = this.formatNumber(minutes)
    const secondsStr = this.formatNumber(seconds)

    const previousValues = {
      days: this.previousDays || daysStr,
      hours: this.previousHours || hoursStr,
      minutes: this.previousMinutes || minutesStr,
      seconds: this.previousSeconds || secondsStr
    }

    this.innerHTML = `
      <div class="countdown-timer countdown-timer--flip-clock">
        <div class="countdown-timer__unit">
          <div class="countdown-timer__flip-box">
            <div class="countdown-timer__flip-top" data-value="${daysStr}" data-previous="${previousValues.days}">
              <span>${daysStr}</span>
            </div>
            <div class="countdown-timer__flip-bottom" data-value="${daysStr}">
              <span>${daysStr}</span>
            </div>
          </div>
          <div class="countdown-timer__label">DAYS</div>
        </div>
        <div class="countdown-timer__separator">:</div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__flip-box">
            <div class="countdown-timer__flip-top" data-value="${hoursStr}" data-previous="${previousValues.hours}">
              <span>${hoursStr}</span>
            </div>
            <div class="countdown-timer__flip-bottom" data-value="${hoursStr}">
              <span>${hoursStr}</span>
            </div>
          </div>
          <div class="countdown-timer__label">HOURS</div>
        </div>
        <div class="countdown-timer__separator">:</div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__flip-box">
            <div class="countdown-timer__flip-top" data-value="${minutesStr}" data-previous="${previousValues.minutes}">
              <span>${minutesStr}</span>
            </div>
            <div class="countdown-timer__flip-bottom" data-value="${minutesStr}">
              <span>${minutesStr}</span>
            </div>
          </div>
          <div class="countdown-timer__label">MINUTES</div>
        </div>
        <div class="countdown-timer__separator">:</div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__flip-box">
            <div class="countdown-timer__flip-top" data-value="${secondsStr}" data-previous="${previousValues.seconds}">
              <span>${secondsStr}</span>
            </div>
            <div class="countdown-timer__flip-bottom" data-value="${secondsStr}">
              <span>${secondsStr}</span>
            </div>
          </div>
          <div class="countdown-timer__label">SECONDS</div>
        </div>
      </div>
    `

    this.previousDays = daysStr
    this.previousHours = hoursStr
    this.previousMinutes = minutesStr
    this.previousSeconds = secondsStr

    this.animateFlip()
  }

  renderDigital(time) {
    const days = Number(time.days) || 0
    const hours = Number(time.hours) || 0
    const minutes = Number(time.minutes) || 0
    const seconds = Number(time.seconds) || 0
    
    const daysStr = this.formatNumber(days)
    const hoursStr = this.formatNumber(hours)
    const minutesStr = this.formatNumber(minutes)
    const secondsStr = this.formatNumber(seconds)

    this.innerHTML = `
      <div class="countdown-timer countdown-timer--digital">
        <div class="countdown-timer__unit">
          <div class="countdown-timer__number">${daysStr}</div>
          <div class="countdown-timer__label">DAYS</div>
        </div>
        <div class="countdown-timer__separator">:</div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__number">${hoursStr}</div>
          <div class="countdown-timer__label">HOURS</div>
        </div>
        <div class="countdown-timer__separator">:</div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__number">${minutesStr}</div>
          <div class="countdown-timer__label">MINUTES</div>
        </div>
        <div class="countdown-timer__separator">:</div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__number">${secondsStr}</div>
          <div class="countdown-timer__label">SECONDS</div>
        </div>
      </div>
    `
  }

  renderText(time) {
    const parts = []
    
    if (time.days > 0) {
      parts.push(`${time.days} ${time.days === 1 ? 'day' : 'days'}`)
    }
    if (time.hours > 0) {
      parts.push(`${time.hours} ${time.hours === 1 ? 'hour' : 'hours'}`)
    }
    if (time.minutes > 0) {
      parts.push(`${time.minutes} ${time.minutes === 1 ? 'minute' : 'minutes'}`)
    }
    if (time.seconds > 0 || parts.length === 0) {
      parts.push(`${time.seconds} ${time.seconds === 1 ? 'second' : 'seconds'}`)
    }

    const text = parts.length > 0 ? parts.join(', ') : '0 seconds'

    this.innerHTML = `
      <div class="countdown-timer countdown-timer--text">
        <div class="countdown-timer__text">${text}</div>
      </div>
    `
  }

  animateFlip() {
    const flipTops = this.querySelectorAll('.countdown-timer__flip-top')
    
    flipTops.forEach((flipTop) => {
      const currentValue = flipTop.dataset.value
      const previousValue = flipTop.dataset.previous
      
      if (previousValue && previousValue !== currentValue) {
        flipTop.classList.add('countdown-timer__flip-top--flip')
        
        setTimeout(() => {
          flipTop.classList.remove('countdown-timer__flip-top--flip')
        }, 600)
      }
    })
  }

  startTimer() {
    this.render()
    
    this.intervalId = setInterval(() => {
      this.render()
    }, 1000)
  }

  stopTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }
}

  if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', CountdownTimer)
  }
}

